import { useMemo } from 'react';
import { useQuery } from '@apollo/client/react';
import { STAFF_PROXY_DEVICE_HEARTBEATS } from '@staff-app/graphql-client';

/** Live device status from open-api's realtime heartbeat cache. */
export type LiveDeviceStatus = 'online' | 'hdmiOff' | 'offline';

interface HeartbeatResult {
  staffProxyDeviceHeartbeats: string; // JSON: [{ key: uuid, value, type }]
}

/** Map open-api heartbeat `type` → status. 0 offline · 1 online · 2 HDMI off. */
function typeToStatus(type: unknown): LiveDeviceStatus {
  const t = String(type ?? '');
  if (t === '1') return 'online';
  if (t === '2') return 'hdmiOff';
  return 'offline';
}

/**
 * Fetch live online/HDMI/offline status for a set of device UUIDs via the staff
 * proxy → open-api `getDeviceHeartBeatByUUIDs`. Returns a UUID→status map plus
 * a `ready` flag; callers fall back to their own (lastHeartBeat) classification
 * while loading or if open-api is unreachable.
 *
 * `pollInterval` defaults to 30s (list views); the My Devices drawer polls its one open device faster.
 */
export function useDeviceHeartbeats(
  uuids: Array<string | undefined | null>,
  pollInterval: number = 30_000,
) {
  // Stable, deduped, sorted key so the query only re-runs when the set changes.
  const list = useMemo(() => {
    const set = new Set<string>();
    for (const u of uuids) if (u) set.add(u);
    return Array.from(set).sort();
  }, [uuids]);

  const { data, loading, error } = useQuery<HeartbeatResult>(STAFF_PROXY_DEVICE_HEARTBEATS, {
    variables: { uuids: list },
    skip: list.length === 0,
    fetchPolicy: 'cache-and-network',
    // Refresh status periodically so the dots reflect live state.
    pollInterval,
  });

  const statusByUuid = useMemo(() => {
    const map = new Map<string, LiveDeviceStatus>();
    const raw = data?.staffProxyDeviceHeartbeats;
    if (!raw) return map;
    try {
      const arr = JSON.parse(raw) as Array<{ key?: string; type?: unknown }>;
      for (const row of arr) {
        if (row?.key) map.set(row.key, typeToStatus(row.type));
      }
    } catch {
      /* malformed payload → empty map, callers fall back */
    }
    return map;
  }, [data]);

  return {
    statusByUuid,
    /** true once we have a usable heartbeat map (so callers can stop falling back). */
    ready: !error && statusByUuid.size > 0,
    loading,
    error,
  };
}
